/**
 * Authentication Storage Service
 * 
 * This service provides typed methods for authentication-related storage operations.
 * Uses sessionStorage for improved security over localStorage.
 * JWT tokens are stored with automatic expiration for added security.
 */

import { sessionStorageService } from '../storage/storage.service.js';
import secureCookieService from '../storage/secure-cookie.service.js';
import { startSession } from '../../components/session/session.js';

// Storage keys used throughout the application
const STORAGE_KEYS = {
    ACCESS_TOKEN: 'access_token',
    USER: 'user',
    BUSINESS_CONDITIONS: 'businessConditions',
    STAFF_PROFILE: 'staffProfile',
    ROLES: 'roles',
    ORGANISATION: 'organisation',
    ACCESS_RIGHTS: 'accessRights'
};

class AuthStorageService {
    constructor(storageBackend = sessionStorageService) {
        this.storage = storageBackend;
        
        // Initialize secure cookies service with error handling
        try {
            this.secureCookies = secureCookieService;
        } catch (error) {
            console.warn('Secure cookie service initialization failed:', error);
            this.secureCookies = null;
        }
    }

        /**
     * Set access token
     * @param {string} token - JWT access token
     * @param {Object} options - Storage options (e.g., expiration)
     * @returns {boolean} Success status
     */
    setAccessToken(token, options = {}) {
        return this.storage.setItem(STORAGE_KEYS.ACCESS_TOKEN, token, options);
    }

    /**
     * Get access token
     * @returns {string|null} Access token or null
     */
    getAccessToken() {
        return this.storage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
    }

    /**
     * Check if user is authenticated
     * @returns {boolean} Authentication status
     */
    isAuthenticated() {
        const token = this.getAccessToken();
        const user = this.getUser();
        
        // Both token and user data must exist
        return token !== null && token !== undefined && token !== '' && user !== null;
    }

    /**
     * Set user data
     * @param {Object} user - User object
     * @returns {boolean} Success status
     */
    setUser(user) {
        return this.storage.setItem(STORAGE_KEYS.USER, user);
    }

    /**
     * Get user data
     * @returns {Object|null} User object or null
     */
    getUser() {
        return this.storage.getItem(STORAGE_KEYS.USER);
    }

    /**
     * Set business conditions
     * @param {Object} conditions - Business conditions object
     * @returns {boolean} Success status
     */
    setBusinessConditions(conditions) {
        return this.storage.setItem(STORAGE_KEYS.BUSINESS_CONDITIONS, conditions);
    }

    /**
     * Get business conditions
     * @returns {Object|null} Business conditions or null
     */
    getBusinessConditions() {
        return this.storage.getItem(STORAGE_KEYS.BUSINESS_CONDITIONS);
    }

    /**
     * Set staff profile
     * @param {Object} profile - Staff profile object
     * @returns {boolean} Success status
     */
    setStaffProfile(profile) {
        return this.storage.setItem(STORAGE_KEYS.STAFF_PROFILE, profile);
    }

    /**
     * Get staff profile
     * @returns {Object|null} Staff profile or null
     */
    getStaffProfile() {
        return this.storage.getItem(STORAGE_KEYS.STAFF_PROFILE);
    }

    /**
     * Get user branch ID from staff profile
     * @returns {string|null} User branch ID or null
     */
    getUserBranchId() {
        const staffProfile = this.getStaffProfile();
        return staffProfile?.userBranchId || null;
    }

    /**
     * Set user roles
     * @param {Array|Object} roles - User roles
     * @returns {boolean} Success status
     */
    setRoles(roles) {
        return this.storage.setItem(STORAGE_KEYS.ROLES, roles);
    }

    /**
     * Get user roles
     * @returns {Array|Object|null} User roles or null
     */
    getRoles() {
        return this.storage.getItem(STORAGE_KEYS.ROLES);
    }

    /**
     * Set organisation data
     * @param {Object} organisation - Organisation object
     * @returns {boolean} Success status
     */
    setOrganisation(organisation) {
        return this.storage.setItem(STORAGE_KEYS.ORGANISATION, organisation);
    }

    /**
     * Get organisation data
     * @returns {Object|null} Organisation object or null
     */
    getOrganisation() {
        return this.storage.getItem(STORAGE_KEYS.ORGANISATION);
    }

    /**
     * Set access rights
     * @param {Array|Object} accessRights - Access rights data
     * @returns {boolean} Success status
     */
    setAccessRights(accessRights) {
        return this.storage.setItem(STORAGE_KEYS.ACCESS_RIGHTS, accessRights);
    }

    /**
     * Get access rights
     * @returns {Array|Object|null} Access rights or null
     */
    getAccessRights() {
        return this.storage.getItem(STORAGE_KEYS.ACCESS_RIGHTS);
    }

    /**
     * Set all authentication data at once (typically after login)
     * @param {Object} authData - Complete authentication data
     * @param {string} authData.token - Access token (stored in sessionStorage)
     * @param {Object} authData.user - User object
     * @param {Object} authData.businessConditions - Business conditions
     * @param {Object} authData.staffProfile - Staff profile
     * @param {Array|Object} authData.roles - User roles
     * @param {Object} authData.organisation - Organisation data
     * @returns {boolean} Success status
     */
    setAuthData(authData) {
        try {
            const operations = [];
            
            // Store token in sessionStorage (more secure than localStorage)
            if (authData.token) {
                operations.push(this.setAccessToken(authData.token));
            }
            
            if (authData.user) {
                operations.push(this.setUser(authData.user));
            }
            
            if (authData.businessConditions) {
                operations.push(this.setBusinessConditions(authData.businessConditions));
            }
            
            if (authData.staffProfile) {
                operations.push(this.setStaffProfile(authData.staffProfile));
            }
            
            if (authData.roles) {
                operations.push(this.setRoles(authData.roles));
            }
            
            if (authData.organisation) {
                operations.push(this.setOrganisation(authData.organisation));
            }

            // Return true only if all operations succeeded
            const success = operations.every(result => result === true);
            
            // Start session timer after successful authentication
            if (success && authData.token) {
                try {
                    startSession();
                    console.log('Session timer started after successful authentication');
                } catch (error) {
                    console.warn('Failed to start session timer:', error);
                }
            }
            
            return success;
        } catch (error) {
            console.error('Error setting authentication data:', error);
            return false;
        }
    }

    /**
     * Get all authentication data
     * Note: JWT token is not included as it's in httpOnly cookie
     * @returns {Object} Complete authentication data object
     */
    getAuthData() {
        return {
            // token: Not accessible - stored in httpOnly cookie
            user: this.getUser(),
            businessConditions: this.getBusinessConditions(),
            staffProfile: this.getStaffProfile(),
            roles: this.getRoles(),
            organisation: this.getOrganisation()
        };
    }

    /**
     * Clear all authentication data (logout)
     * Clears session storage synchronously and attempts server logout in background
     * @returns {boolean} Success status for session storage clearing
     */
    clearAuthData() {
        // Clear session storage data
        const keys = [
            STORAGE_KEYS.ACCESS_TOKEN, // For backward compatibility
            STORAGE_KEYS.USER,
            STORAGE_KEYS.BUSINESS_CONDITIONS,
            STORAGE_KEYS.STAFF_PROFILE,
            STORAGE_KEYS.ROLES,
            STORAGE_KEYS.ORGANISATION
        ];

        const sessionCleared = this.storage.removeItems(keys);
        
        // Attempt to clear httpOnly cookie via server in background (non-blocking)
        this._clearServerCookiesAsync().catch(error => {
            console.warn('Background server logout failed:', error);
        });
        
        return sessionCleared;
    }

    /**
     * Clear all authentication data (logout) - async version
     * Clears session storage and waits for server logout completion
     * @returns {Promise<boolean>} Success status for both session and server clearing
     */
    async clearAuthDataAsync() {
        // Clear session storage data
        const keys = [
            STORAGE_KEYS.ACCESS_TOKEN, // For backward compatibility
            STORAGE_KEYS.USER,
            STORAGE_KEYS.BUSINESS_CONDITIONS,
            STORAGE_KEYS.STAFF_PROFILE,
            STORAGE_KEYS.ROLES,
            STORAGE_KEYS.ORGANISATION
        ];

        const sessionCleared = this.storage.removeItems(keys);
        
        // Try to clear httpOnly cookie via server (optional)
        let cookieCleared = true;
        try {
            cookieCleared = await this._clearServerCookiesAsync();
        } catch (error) {
            console.warn('Server logout failed, but session storage cleared successfully:', error);
            // Don't fail the logout if server-side logout fails
            cookieCleared = true;
        }
        
        return sessionCleared && cookieCleared;
    }

    /**
     * Private method to clear server-side cookies
     * @returns {Promise<boolean>} Success status
     * @private
     */
    async _clearServerCookiesAsync() {
        if (this.secureCookies && typeof this.secureCookies.logout === 'function') {
            return await this.secureCookies.logout();
        } else {
            console.warn('Secure cookies service not available, skipping server logout');
            return true;
        }
    }    /**
     * Clear all authentication data (logout) - synchronous version
     * Only clears session storage, not httpOnly cookie
     * @returns {boolean} Success status
     */
    clearAuthDataSync() {
        const keys = [
            STORAGE_KEYS.ACCESS_TOKEN,
            STORAGE_KEYS.USER,
            STORAGE_KEYS.BUSINESS_CONDITIONS,
            STORAGE_KEYS.STAFF_PROFILE,
            STORAGE_KEYS.ROLES,
            STORAGE_KEYS.ORGANISATION
        ];

        return this.storage.removeItems(keys);
    }

    /**
     * Clear only session-related data (partial logout)
     * @returns {boolean} Success status
     */
    clearSessionData() {
        const keys = [
            STORAGE_KEYS.ACCESS_TOKEN,
            STORAGE_KEYS.USER
        ];

        return this.storage.removeItems(keys);
    }

    /**
     * Check if authentication data is complete
     * @returns {Object} Validation result with missing fields
     */
    validateAuthData() {
        const authData = this.getAuthData();
        const missingFields = [];

        // Note: JWT token is not checked as it's in httpOnly cookie
        if (!authData.user) missingFields.push('user');
        if (!authData.staffProfile) missingFields.push('staffProfile');

        return {
            isValid: missingFields.length === 0,
            missingFields,
            authData
        };
    }

    /**
     * Get authentication summary for debugging
     * @returns {Object} Authentication summary
     */
    getAuthSummary() {
        const authData = this.getAuthData();
        const validation = this.validateAuthData();
        
        return {
            isAuthenticated: this.isAuthenticated(),
            hasUser: !!authData.user,
            hasStaffProfile: !!authData.staffProfile,
            hasRoles: !!authData.roles,
            hasBusinessConditions: !!authData.businessConditions,
            hasOrganisation: !!authData.organisation,
            userBranchId: this.getUserBranchId(),
            hasToken: !!authData.token,
            validation,
            storageInfo: this.storage.getStorageInfo()
        };
    }

    /**
     * Get authentication summary for debugging (synchronous)
     * @returns {Object} Authentication summary
     */
    getAuthSummarySync() {
        const authData = this.getAuthData();
        const validation = this.validateAuthData();
        
        return {
            isAuthenticatedSync: this.isAuthenticatedSync(),
            hasUser: !!authData.user,
            hasStaffProfile: !!authData.staffProfile,
            hasRoles: !!authData.roles,
            hasBusinessConditions: !!authData.businessConditions,
            hasOrganisation: !!authData.organisation,
            userBranchId: this.getUserBranchId(),
            validation,
            storageInfo: this.storage.getStorageInfo(),
            storageType: 'sessionStorage + httpOnly cookies'
        };
    }

    /**
     * Switch the underlying storage backend
     * @param {StorageService} newStorage - New storage service instance
     */
    switchStorage(newStorage) {
        this.storage = newStorage;
    }
}

// Export storage keys for use in other parts of the application
export { STORAGE_KEYS };

// Create and export the default auth storage service
const authStorageService = new AuthStorageService();

export { AuthStorageService };
export default authStorageService;
